import { useRef, useState } from "react";
import { Sigma, Eye, EyeOff } from "lucide-react";
import MathText from "./MathText";

/**
 * Math-aware author field. Wraps a textarea (or a single-line input) with:
 *  - a quick-insert toolbar for common LaTeX snippets
 *  - a live KaTeX preview toggle
 *  - an inline hint about $...$ / $$...$$
 *
 * Fully controlled: parent owns the string value.
 */
const SNIPPETS = [
  { label: "π", tex: "\\pi" },
  { label: "θ", tex: "\\theta" },
  { label: "α", tex: "\\alpha" },
  { label: "β", tex: "\\beta" },
  { label: "λ", tex: "\\lambda" },
  { label: "μ", tex: "\\mu" },
  { label: "Σ", tex: "\\sum_{i=1}^{n}" },
  { label: "∫", tex: "\\int_{a}^{b}" },
  { label: "√", tex: "\\sqrt{x}" },
  { label: "x²", tex: "x^{2}" },
  { label: "x/y", tex: "\\frac{x}{y}" },
  { label: "→", tex: "\\rightarrow" },
  { label: "≤", tex: "\\leq" },
  { label: "≥", tex: "\\geq" },
  { label: "≠", tex: "\\neq" },
  { label: "±", tex: "\\pm" },
  { label: "∞", tex: "\\infty" },
  { label: "vec", tex: "\\vec{v}" },
];

export default function MathAuthorField({
  value,
  onChange,
  placeholder,
  rows = 3,
  multiline = true,
  compact = false,
  showToolbar = true,
  showPreview = true,
  className = "",
  testIdBase,
}) {
  const ref = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(showPreview);

  const insertAtCursor = (snippet, wrap = "$") => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const hasSelection = end > start;
    const selectedText = value.slice(start, end);
    const insertion = hasSelection
      ? `${wrap}${selectedText}${wrap}`
      : `${wrap}${snippet}${wrap}`;
    const next = value.slice(0, start) + insertion + value.slice(end);
    onChange(next);
    // Restore focus + place cursor between wrappers
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + insertion.length;
      try { el.setSelectionRange(pos, pos); } catch { /* IE / non-input elements */ }
    });
  };

  const InputEl = multiline ? "textarea" : "input";
  const inputProps = multiline ? { rows } : { type: "text" };

  return (
    <div className={className} data-testid={testIdBase ? `${testIdBase}-wrap` : undefined}>
      {showToolbar && (
        <div className="flex items-center flex-wrap gap-1 mb-1.5" data-testid={testIdBase ? `${testIdBase}-toolbar` : undefined}>
          <button
            type="button"
            onClick={() => insertAtCursor("x", "$")}
            className="text-[11px] font-semibold px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-100"
            title="Wrap selection in inline math $...$"
            data-testid={testIdBase ? `${testIdBase}-wrap-inline` : undefined}
          >
            $x$
          </button>
          <button
            type="button"
            onClick={() => insertAtCursor("x", "$$")}
            className="text-[11px] font-semibold px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-100"
            title="Wrap selection in block math $$...$$"
            data-testid={testIdBase ? `${testIdBase}-wrap-block` : undefined}
          >
            $$x$$
          </button>
          <span className="w-px h-4 bg-slate-200 mx-1" />
          <div className="flex flex-wrap gap-1">
            {SNIPPETS.slice(0, compact ? 8 : SNIPPETS.length).map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => insertAtCursor(s.tex, "$")}
                className="text-[11px] font-semibold px-1.5 py-1 rounded-md bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"
                title={`Insert ${s.tex}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setPreviewOpen((o) => !o)}
            className="ml-auto text-[11px] font-semibold px-2 py-1 rounded-md bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 flex items-center gap-1"
            data-testid={testIdBase ? `${testIdBase}-preview-toggle` : undefined}
          >
            {previewOpen ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {previewOpen ? "Hide preview" : "Preview"}
          </button>
        </div>
      )}
      <InputEl
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm"
        data-testid={testIdBase || undefined}
        {...inputProps}
      />
      {previewOpen && (
        <div
          className="mt-2 p-2.5 rounded-lg bg-white border border-slate-100 text-sm text-slate-800 leading-relaxed min-h-[2.25rem]"
          data-testid={testIdBase ? `${testIdBase}-preview` : undefined}
        >
          {value ? <MathText>{value}</MathText> : <span className="text-slate-400 italic">Preview will appear here…</span>}
        </div>
      )}
      {showToolbar && (
        <p className="mt-1 text-[10px] text-slate-400 flex items-center gap-1">
          <Sigma className="w-2.5 h-2.5" /> Wrap math in <code className="mx-0.5">$…$</code> for inline or <code className="mx-0.5">$$…$$</code> for block equations.
        </p>
      )}
    </div>
  );
}
